import { GAMEPLAY } from '@/config/gameplay';
import { distanceM, type LatLng } from '@/game/geo';
import type { ActivityType } from '@/types';

export type RoutePreference = 'fast' | 'protected' | 'quiet';
export type TerrainPreference = 'flat' | 'balanced' | 'hilly';

/** Route-quality settings shared by destination planning and generated missions. */
export interface RouteQualitySettings {
  preference: RoutePreference;
  terrain: TerrainPreference;
  preferCycleways: boolean;
}

export const DEFAULT_ROUTE_QUALITY_SETTINGS: RouteQualitySettings = {
  preference: 'fast',
  terrain: 'balanced',
  preferCycleways: false,
};

/** Enabled only together with an elevation-capable production GraphHopper graph. */
export const ROUTE_ELEVATION_ENABLED = import.meta.env.VITE_ROUTE_ELEVATION_ENABLED === 'true';

/**
 * What the planner actually receives. The route character (Gyors / Védett út /
 * Nyugalom) goes for every activity since #75 (Geri): on foot the server has
 * dedicated rules — main-road penalty, footway bonus, and for Nyugalom the
 * busy-road zones (corpus: walking quiet 0.80–0.89, protected 0.99). The
 * cycleway preference stays bike-only.
 */
export function effectiveRouteQualitySettings(
  settings: RouteQualitySettings,
  activityType: ActivityType,
): RouteQualitySettings {
  const isBike = activityType === 'ride';
  return {
    preference: settings.preference,
    terrain: ROUTE_ELEVATION_ENABLED ? settings.terrain : 'balanced',
    preferCycleways: isBike && settings.preferCycleways,
  };
}

/**
 * The start and destination are the same place: the user wants a round trip
 * (Geri, #86). Same threshold as the server (`routes.ts` → `parseInput`).
 */
export function isRoundTrip(from: LatLng, to: LatLng): boolean {
  return distanceM(from, to) < GAMEPLAY.ROUTE_SAME_POINT_M;
}
